import { PrismaticJwtToken } from '@/entities/prismaticJwtToken';
import { ApiError } from '@/services/api';
import { create as createPrismaticJwt } from '@/services/api/resources/prismatic-jwt-token/actions';
import { fileToBase64 } from '@/services/file/fileProcessor';
import { IntegrationDetailed } from '@/services/integrations/types/integrationDetailed';
import * as E from 'fp-ts/Either';
import createInstanceQuery from './graphQl/createInstance.graphql';
import deployInstanceQuery from './graphQl/deployInstance.graphql';
import deleteInstanceQuery from './graphQl/deleteInstance.graphql';
import enableDisableQuery from './graphQl/enableDisable.graphql';
import integrationQuery from './graphQl/integration.graphql';
import integrationsQuery from './graphQl/integrations.graphql';
import instanceConnectionStatusQuery from './graphQl/instanceConnectionStatus.graphql';
import instanceConfigVariablesMeta from './graphQl/instanceConfigVariablesMeta.graphql';
import logsQuery from './graphQl/logs.graphql';
import updateInstanceConfigVariables from './graphQl/updateInstanceConfigVariables.graphql';
import updateInstanceVersion from './graphQl/updateInstanceVersion.graphql';
import {
  GraphqlRequestProps,
  InputInstanceConfigVariable,
  Instance,
  InstanceConnectionStatus,
  Integration,
  IntegrationID,
  Logs,
  ResponseWithErrors,
  InstanceConfigVariablesMeta,
} from './types';

class PrismaticClient {
  private token: string|undefined;
  private customerId: string|undefined;
  private readonly prismaticUrl = `${process.env.VUE_APP_PRISMATIC_URL}`;
  private readonly authenticateUrl = '/embedded/authenticate';

  public async getIntegrations(): Promise<Integration[]> {
    const result = await this.graphQlCall({
      query: integrationsQuery,
      variables: {
        label: process.env.NODE_ENV,
      },
    });
    return result.data.marketplaceIntegrations.nodes;
  }

  public async getIntegration(id: string): Promise<IntegrationDetailed> {
    const result = await this.graphQlCall({
      query: integrationQuery,
      variables: { id },
    });
    return result.data.marketplaceIntegration;
  }

  public async getLogs(
    instanceId: string,
    startDate: Date|null = null,
    endDate: Date|null = null,
    searchTerm: string|null = null,
    limit: number|null = null,
    offset: number|null = null,
  ): Promise<Logs> {
    const result = await this.graphQlCall({
      query: logsQuery,
      variables: {
        instanceId,
        limit,
        offset,
        startDate: startDate?.toISOString(),
        endDate: endDate?.toISOString(),
        searchTerm,
      },
    });
    return result.data.logs;
  }

  /**
   * "integration.avatarUrl" contains Prismatic API url.
   * This API returns "{url: string}" object with Amazon S3 signed URL with very short validity time
   * We resolve the Amazon S3 URL to base64 data URL to avoid timeout issues
   */
  public async resolveAvatarUrl(avatarUrl: string): Promise<string> {
    const result = await this.getCall(avatarUrl);
    const image = await fetch(result.url);
    return fileToBase64(await image.blob());
  }

  public enableDisableInstance(instanceId: string, enabled: boolean): Promise<ResponseWithErrors> {
    return this.graphQlCall({
      query: enableDisableQuery,
      variables: {
        instanceId,
        enabled,
      },
    });
  }

  public updateInstanceVersion(instanceId: string, integrationId: IntegrationID): Promise<ResponseWithErrors> {
    return this.graphQlCall({
      query: updateInstanceVersion,
      variables: {
        instanceId,
        integrationId,
      },
    });
  }

  public async createInstance(integrationId: IntegrationID, instanceName: string, configVariables?: InputInstanceConfigVariable[]): Promise<Instance> {
    const customerId = await this.getPrismaticCustomerId();

    const response = await this.graphQlCall({
      query: createInstanceQuery,
      variables: {
        customerId,
        integrationId,
        instanceName,
        configVariables,
      },
    });

    return response.data.createInstance.instance;
  }

  public async deployInstance(instanceId: string): Promise<Instance> {
    const response = await this.graphQlCall({
      query: deployInstanceQuery,
      variables: {
        instanceId,
      },
    });

    return response.data.deployInstance.instance;
  }

  public updateInstanceConfigVariables(
    instanceId: string,
    configVariables: InputInstanceConfigVariable[],
    configMode = 'INSTANCE',
    configComplete: boolean|null = null,
    clientMutationId: string|null = null,
  ): Promise<ResponseWithErrors> {
    return this.graphQlCall({
      query: updateInstanceConfigVariables,
      variables: {
        instanceId,
        configVariables,
        configMode,
        configComplete,
        clientMutationId,
      },
    });
  }

  public async getInstanceConnectionStatus(instanceId: string): Promise<InstanceConnectionStatus> {
    const response = await this.graphQlCall({
      query: instanceConnectionStatusQuery,
      variables: {
        id: instanceId,
      },
    });

    return response.data.instance;
  }

  public async getInstanceConfigVariablesMeta(instanceId: string): Promise<InstanceConfigVariablesMeta> {
    const response = await this.graphQlCall({
      query: instanceConfigVariablesMeta,
      variables: {
        id: instanceId,
      },
    });

    return response.data.instance;
  }

  public deleteInstance(instanceId: string): Promise<ResponseWithErrors> {
    return this.graphQlCall({
      query: deleteInstanceQuery,
      variables: {
        instanceId,
      },
    });
  }

  private async initializePrismaticJwt(): Promise<void> {
    const resultEither = await createPrismaticJwt()();
    const jwtTokenResponse = E.getOrElse<ApiError, PrismaticJwtToken>(error => {
      throw error;
    })(resultEither);

    const token = jwtTokenResponse.access_token;
    await this.authenticateJwtToken(token);

    this.token = token;
    this.customerId = jwtTokenResponse.prismatic_customer_id;
  }

  private async getPrismaticJwt(): Promise<string> {
    if (!this.token) {
      await this.initializePrismaticJwt();
    }

    return this.token as string;
  }

  private async getPrismaticCustomerId(): Promise<string> {
    if (!this.customerId) {
      await this.initializePrismaticJwt();
    }

    return this.customerId as string;
  }

  private async graphQlCall(request: GraphqlRequestProps) {
    const token = await this.getPrismaticJwt();
    const response = await fetch(`${this.prismaticUrl}/api/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(request),
    });

    return response.json();
  }

  private async getCall(url: string) {
    const token = await this.getPrismaticJwt();
    const response = await fetch(`${this.prismaticUrl}${url}`, {
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    return response.json();
  }

  private async authenticateJwtToken(token: string) {
    await fetch(`${this.prismaticUrl}${this.authenticateUrl}`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }
}

export default new PrismaticClient;
